// NoteCard v7 — mobile-friendly submenu positioning
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Pin, Trash2, MoreHorizontal, FolderInput, Copy, Check, X, AlertTriangle, Share2 } from 'lucide-react';
import { useStore } from '../../store';
import { Badge } from '../ui';
import { truncate } from '../../utils';
import { useRelativeTime } from '../../hooks';
import { toast } from '../ui/Toast';
import { notesApi } from '../../api/client';
import type { Note } from '../../types';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', muted:'#7A7570', border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

// ─── Delete Modal via Portal ──────────────────────────────────────────────────
const DeleteModal: React.FC<{ onConfirm:()=>void; onCancel:()=>void }> = ({ onConfirm, onCancel }) => {
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return ReactDOM.createPortal(
    <div onClick={stop} onMouseDown={stop} style={{ position:'fixed', inset:0, zIndex:99999 }}>
      <div onClick={(e)=>{ stop(e); onCancel(); }} style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(4px)' }}/>
      <div onClick={stop} style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'min(90vw,400px)', background:C.white, borderRadius:20, padding:'28px 28px 22px', boxShadow:'0 24px 64px rgba(0,0,0,0.25)', zIndex:1 }}>
        <button onClick={onCancel} style={{ position:'absolute', top:14, right:14, background:'none', border:'none', cursor:'pointer', color:C.muted, padding:4, display:'flex' }}><X size={16}/></button>
        <div style={{ width:50, height:50, borderRadius:14, background:'#FEF2F2', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
          <AlertTriangle size={24} color="#DC2626"/>
        </div>
        <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:19, color:C.ink, marginBottom:8 }}>Delete this note?</h2>
        <p style={{ fontSize:14, color:C.muted, lineHeight:1.75, fontFamily:font.body, marginBottom:24 }}>
          This is <strong style={{ color:C.ink }}>permanent and cannot be undone.</strong> The note and all its content will be deleted forever.
        </p>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onCancel} style={{ flex:'0 0 90px', height:40, borderRadius:10, border:`1.5px solid ${C.border}`, background:C.surface, cursor:'pointer', fontFamily:font.body, fontWeight:600, fontSize:13, color:'#3D3A34' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex:1, height:40, borderRadius:10, border:'none', background:'linear-gradient(135deg,#EF4444,#DC2626)', cursor:'pointer', fontFamily:font.body, fontWeight:700, fontSize:14, color:C.white, boxShadow:'0 4px 14px #DC262640' }}>Yes, delete forever</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Floating Menu via Portal ─────────────────────────────────────────────────
interface MenuProps {
  note: Note;
  anchorRect: DOMRect;
  onClose: () => void;
  onDelete: () => void;
}

const FloatingMenu: React.FC<MenuProps> = ({ note, anchorRect, onClose, onDelete }) => {
  const pinNote    = useStore(s => s.pinNote);
  const updateNote = useStore(s => s.updateNote);
  const createNote = useStore(s => s.createNote);
  const folders    = useStore(s => s.folders);
  const [showMove, setShowMove] = useState(false);
  const menuRef    = useRef<HTMLDivElement>(null);

  const menuWidth  = 174;
  const submenuWidth = 168;
  const isMobile   = window.innerWidth < 600;

  // Menu position — above or below anchor button
  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const menuHeight = 220;
  const menuTop    = spaceBelow > menuHeight
    ? anchorRect.bottom + 4
    : anchorRect.top - menuHeight - 4;

  // Keep menu within screen horizontally
  const menuLeft = Math.min(
    Math.max(8, anchorRect.right - menuWidth),
    window.innerWidth - menuWidth - 8
  );

  // Submenu position — right of menu on desktop, below on mobile
  const spaceRight = window.innerWidth - (menuLeft + menuWidth);
  const openRight  = !isMobile && spaceRight >= submenuWidth + 8;

  const submenuTop  = openRight
    ? menuTop
    : menuTop + 40; // below the "Move to folder" row

  const submenuLeft = openRight
    ? menuLeft + menuWidth + 4
    : Math.max(8, Math.min(menuLeft, window.innerWidth - submenuWidth - 8));

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [onClose]);

  const moveToFolder = async (folderId: string | null) => {
    await updateNote(note.id, { folderId });
    toast.success(`Moved to ${folderId ? folders.find(f=>f.id===folderId)?.name : 'Unfiled'}`);
    onClose();
  };

  const copyNote = async () => {
    onClose();
    const copy = await createNote(note.folderId);
    await updateNote(copy.id, { title:`${note.title} (copy)`, body:note.body, plainText:note.plainText, tags:[...note.tags] });
    toast.success('Note copied!');
  };

  const shareNote = async () => {
    onClose();
    try {
      const res = await notesApi.share(note.id) as any;
      await navigator.clipboard.writeText(res.shareUrl);
      toast.success('Share link copied to clipboard!');
    } catch { toast.error('Could not create share link'); }
  };

  return ReactDOM.createPortal(
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:9998 }}/>

      <div ref={menuRef} style={{ position:'fixed', top:menuTop, left:menuLeft, background:C.white, border:`1px solid ${C.border}`, borderRadius:12, padding:6, minWidth:menuWidth, boxShadow:'0 8px 32px rgba(0,0,0,0.16)', zIndex:9999 }}>

        <Btn icon={<Pin size={13}/>} label={note.isPinned ? 'Unpin note' : 'Pin note'}
          onClick={() => { pinNote(note.id, !note.isPinned); onClose(); }}/>

        <Btn icon={<Copy size={13}/>} label="Copy note" onClick={copyNote}/>

        <Btn icon={<Share2 size={13}/>} label="Share note" onClick={shareNote}/>

        {/* Move to folder */}
        <Btn
          icon={<FolderInput size={13}/>}
          label="Move to folder"
          suffix={openRight ? '›' : showMove ? '▲' : '▼'}
          active={showMove}
          onClick={() => setShowMove(s => !s)}
        />

        {/* Submenu — inline below on mobile, fixed right on desktop */}
        {showMove && (
          openRight ? (
            // Desktop — render to the right via portal positioning
            ReactDOM.createPortal(
              <div style={{ position:'fixed', top:submenuTop, left:submenuLeft, background:C.white, border:`1px solid ${C.border}`, borderRadius:10, padding:6, minWidth:submenuWidth, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', zIndex:10000, maxHeight:220, overflowY:'auto' }}>
                <FolderBtn label="Unfiled" color="#7A7570" active={!note.folderId} onClick={() => moveToFolder(null)}/>
                {folders.map(f => (
                  <FolderBtn key={f.id} label={f.name} color={f.color} active={note.folderId === f.id} onClick={() => moveToFolder(f.id)}/>
                ))}
              </div>,
              document.body
            )
          ) : (
            // Mobile — render inline inside the menu, no off-screen risk
            <div style={{ background:C.surface, borderRadius:8, margin:'2px 0', padding:'2px 0', maxHeight:180, overflowY:'auto', borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}` }}>
              <FolderBtn label="Unfiled" color="#7A7570" active={!note.folderId} onClick={() => moveToFolder(null)}/>
              {folders.map(f => (
                <FolderBtn key={f.id} label={f.name} color={f.color} active={note.folderId === f.id} onClick={() => moveToFolder(f.id)}/>
              ))}
            </div>
          )
        )}

        <div style={{ height:1, background:C.border, margin:'4px 6px' }}/>

        <Btn icon={<Trash2 size={13}/>} label="Delete note" danger
          onClick={() => { onClose(); onDelete(); }}/>
      </div>
    </>,
    document.body
  );
};

// ─── Note Card ────────────────────────────────────────────────────────────────
interface Props { note: Note; searchQuery?: string }

const NoteCard: React.FC<Props> = ({ note, searchQuery }) => {
  const setOpenNote = useStore(s => s.setOpenNote);
  const deleteNote  = useStore(s => s.deleteNote);
  const folders     = useStore(s => s.folders);

  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null);
  const [showModal,  setShowModal]  = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const folder = folders.find(f => f.id === note.folderId);
  const time   = useRelativeTime(note.updatedAt);

  const preview = () => {
    const text = truncate(note.plainText || note.title, 120);
    if (!searchQuery) return <span style={{ color:C.muted }}>{text}</span>;
    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
    return parts.map((p, i) =>
      p.toLowerCase() === searchQuery.toLowerCase()
        ? <mark key={i} style={{ background:C.amber200, color:C.amber500, borderRadius:2, padding:'0 1px' }}>{p}</mark>
        : <span key={i} style={{ color:C.muted }}>{p}</span>
    );
  };

  const openMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (btnRef.current) setMenuAnchor(btnRef.current.getBoundingClientRect());
  };

  return (
    <>
      <div
        onClick={() => { if (!showModal && !menuAnchor) setOpenNote(note.id); }}
        style={{ background:C.white, border:`1px solid ${note.isPinned?C.amber200:C.border}`, borderRadius:14, padding:'16px 18px', cursor:'pointer', position:'relative', transition:'box-shadow .15s, transform .15s' }}
        onMouseEnter={e=>{ (e.currentTarget as HTMLDivElement).style.boxShadow='0 6px 20px rgba(0,0,0,0.08)'; (e.currentTarget as HTMLDivElement).style.transform='translateY(-1px)'; }}
        onMouseLeave={e=>{ (e.currentTarget as HTMLDivElement).style.boxShadow='none'; (e.currentTarget as HTMLDivElement).style.transform=''; }}
      >
        {note.isPinned && <div style={{ position:'absolute', top:12, right:14 }}><Pin size={12} color={C.amber400} fill={C.amber400}/></div>}

        <p style={{ fontSize:14, fontWeight:700, color:C.ink, fontFamily:font.head, marginBottom:6, paddingRight:20, lineHeight:1.3, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
          {note.title || 'Untitled note'}
        </p>
        <p style={{ fontSize:12, lineHeight:1.6, marginBottom:12, fontFamily:font.body, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical' }}>
          {preview()}
        </p>
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', paddingRight:28 }}>
          {folder && <Badge color={folder.color}>{folder.name}</Badge>}
          {note.tags.slice(0,2).map(t => <Badge key={t} color={C.amber400}>#{t}</Badge>)}
          <span style={{ marginLeft:'auto', fontSize:10, color:C.muted, fontFamily:font.body, whiteSpace:'nowrap' }}>{time}</span>
        </div>

        <div style={{ position:'absolute', bottom:10, right:10 }} onClick={e=>e.stopPropagation()}>
          <button ref={btnRef} onClick={openMenu}
            style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:6, display:'flex', borderRadius:6 }}>
            <MoreHorizontal size={16}/>
          </button>
        </div>
      </div>

      {menuAnchor && (
        <FloatingMenu
          note={note}
          anchorRect={menuAnchor}
          onClose={() => setMenuAnchor(null)}
          onDelete={() => setShowModal(true)}
        />
      )}

      {showModal && (
        <DeleteModal
          onConfirm={() => { deleteNote(note.id); setShowModal(false); }}
          onCancel={() => setShowModal(false)}
        />
      )}
    </>
  );
};

// ─── Sub components ───────────────────────────────────────────────────────────
const Btn: React.FC<{ icon:React.ReactNode; label:string; onClick:()=>void; danger?:boolean; suffix?:string; active?:boolean }> = ({ icon, label, onClick, danger, suffix, active }) => (
  <button onClick={onClick}
    style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, width:'100%', padding:'9px 10px', borderRadius:7, border:'none', background:active?C.amber100:'none', cursor:'pointer', fontSize:13, fontFamily:font.body, color:danger?'#DC2626':'#3D3A34' }}
    onMouseEnter={e=>{ if(!active)(e.currentTarget as HTMLButtonElement).style.background=C.surface; }}
    onMouseLeave={e=>{ if(!active)(e.currentTarget as HTMLButtonElement).style.background='none'; }}>
    <span style={{ display:'flex', alignItems:'center', gap:8 }}>{icon}{label}</span>
    {suffix && <span style={{ color:C.muted, fontSize:11 }}>{suffix}</span>}
  </button>
);

const FolderBtn: React.FC<{ label:string; color:string; active:boolean; onClick:()=>void }> = ({ label, color, active, onClick }) => (
  <button onClick={onClick}
    style={{ display:'flex', alignItems:'center', gap:8, width:'100%', padding:'8px 10px', borderRadius:7, border:'none', background:active?C.amber100:'none', cursor:'pointer', fontSize:13, fontFamily:font.body, color:'#3D3A34' }}
    onMouseEnter={e=>{ if(!active)(e.currentTarget as HTMLButtonElement).style.background=C.surface; }}
    onMouseLeave={e=>{ if(!active)(e.currentTarget as HTMLButtonElement).style.background='none'; }}>
    <div style={{ width:10, height:10, borderRadius:3, background:color, flexShrink:0 }}/>
    <span style={{ flex:1, textAlign:'left', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</span>
    {active && <Check size={11} color={C.amber400}/>}
  </button>
);

export default NoteCard;